/**
 * The thirty-four named squadrons: ten elite, twenty-four ordinary.
 *
 * Names, nations, the elite/ordinary split and the ace associations are
 * docs/game/aces-squadrons.md §4's own tables, which come from `ELITE.DAT` /
 * `ORDINARY.DAT` and the manual. The elite ten are transfer targets rather than
 * starting postings — *"If you're doing very well … you may have the chance to
 * fly with No. 56 Squadron"*, *"you may attain enough clout to fly with
 * J.G.1"* (manual).
 *
 * **Station assignment is port-side.** That document is explicit that no static
 * squadron-to-aerodrome table was found and that the game appears to simulate
 * station changes; what is not invented is the *aerodrome list itself*, which
 * is `AERO.DAT`'s real forty fields (`aerodromes.ts`). Where a unit has a
 * well-known historical field in the file — 56 Squadron at Vert Galant, Jasta
 * 11 at Douai, J.G.1 at Cappy, the 94th Aero at Toul, 39 Home Defence at
 * Sutton's Farm — that is the one used.
 *
 * `activeFrom` / `activeUntil` keep a December 1915 German career out of a
 * Jagdstaffel that did not exist until August 1916, and out of the
 * *Kampfeinsitzerkommando* units after they were folded into it. Same posture:
 * a real historical fact used to pick between real named units, not a
 * mechanic the original is claimed to have.
 *
 * **The ace associations are no longer port-side.** They were hand-written
 * "historically tied to the unit" guesses until `ELITE.DAT` and `ORDINARY.DAT`
 * were decoded (`squadron-strength.ts`), which carry a famous-ace byte per
 * squadron per month. `aces` is now *derived* from that decode rather than
 * restated beside it, so there is one source of truth and it is the game's own.
 * The correction is real: the file puts Bishop on No.85 Squadron rather than
 * No.60, Udet on Jasta 37 rather than Schleich, Goering on Jasta 27 (which had
 * no ace here at all), and gives No.56, No.74 and the Storks no resident ace —
 * their famous names are reachable through `FAMOUS_ACES` and Dogfight a Famous
 * Ace, but the squadron table never stationed them.
 *
 * Nothing read this field before — it was declared and unused — so deriving it
 * moves no existing draw.
 */

import type { Nation } from "../mission/mission-roster.ts";
import type { MissionRegion } from "../mission/mission-world.ts";
import { requireAerodrome } from "./aerodromes.ts";
import { SQUADRON_STRENGTH } from "./squadron-strength.ts";
import { compareDates, monthIndexOf, type CareerDate } from "./calendar.ts";

/** The `FAMOUS_ACES` ids `ELITE.DAT`/`ORDINARY.DAT` station with this unit, across every era it flew. */
function decodedAces(id: string): readonly string[] {
  const eras = SQUADRON_STRENGTH[id];
  if (!eras) return [];
  const found: string[] = [];
  for (const strength of Object.values(eras)) {
    for (const ace of strength.aces) if (!found.includes(ace)) found.push(ace);
  }
  return found;
}

/** Which arm a unit belongs to. Only `rfc`, `rnas` and `gas` take player pilots. */
export const SQUADRON_SERVICES = ["rfc", "rnas", "gas", "french", "american"] as const;

export type SquadronService = (typeof SQUADRON_SERVICES)[number];

export type SquadronRole = "fighter" | "recon";

export interface Squadron {
  readonly id: string;
  readonly name: string;
  readonly nation: Nation;
  readonly service: SquadronService;
  readonly elite: boolean;
  readonly role: SquadronRole;
  /** An `AERO.DAT` field name. */
  readonly aerodrome: string;
  readonly region: MissionRegion;
  /** `mission-roster.ts` ace ids historically tied to the unit. */
  readonly aces: readonly string[];
  readonly activeFrom: CareerDate | undefined;
  readonly activeUntil: CareerDate | undefined;
}

interface SquadronInit {
  readonly role?: SquadronRole;
  readonly activeFrom?: CareerDate;
  readonly activeUntil?: CareerDate;
}

function squadron(
  id: string,
  name: string,
  nation: Nation,
  service: SquadronService,
  elite: boolean,
  aerodrome: string,
  init: SquadronInit = {},
): Squadron {
  return {
    id,
    name,
    nation,
    service,
    elite,
    role: init.role ?? "fighter",
    aerodrome,
    region: requireAerodrome(aerodrome).region,
    aces: decodedAces(id),
    activeFrom: init.activeFrom,
    activeUntil: init.activeUntil,
  };
}

/** Jagdstaffeln were formed from August 1916; the KEKs were folded into them. */
const JASTA_ERA: CareerDate = { month: monthIndexOf(1916, 7), day: 1 };
const KEK_ERA_END: CareerDate = { month: monthIndexOf(1916, 8), day: 1 };
const RAF_SQUADRON_ERA: CareerDate = { month: monthIndexOf(1918, 2), day: 1 };

export const SQUADRONS: readonly Squadron[] = [
  squadron("sqn-24", "No.24 Squadron", "allied", "rfc", true, "Bertangles"),
  squadron("sqn-56", "No.56 Squadron", "allied", "rfc", true, "Vert Galant"),
  squadron("sqn-74", "No.74 Squadron", "allied", "rfc", true, "St. Omer", { activeFrom: RAF_SQUADRON_ERA }),
  squadron("black-flight", "The Black Flight", "allied", "rnas", true, "Furnes"),
  squadron("storks", "The Storks", "allied", "french", true, "Villers-Cotterets"),
  squadron("aero-94", "94th Aero Squadron", "allied", "american", true, "Toul"),
  squadron("flabt-203", "Fl. Abt. No. 203", "german", "gas", true, "Sivry", { role: "recon" }),
  squadron("jasta-2", "Jasta 2", "german", "gas", true, "Cambrai", { activeFrom: JASTA_ERA }),
  squadron("jasta-11", "Jasta 11", "german", "gas", true, "Douai", { activeFrom: JASTA_ERA }),
  squadron("jg-1", "JG 1", "german", "gas", true, "Cappy", {
    activeFrom: { month: monthIndexOf(1917, 5), day: 1 },
  }),
  squadron("sqn-3", "No.3 Squadron", "allied", "rfc", false, "St. Pol"),
  squadron("sqn-11", "No.11 Squadron", "allied", "rfc", false, "Bertangles"),
  squadron("sqn-40", "No.40 Squadron", "allied", "rfc", false, "La Gorgue"),
  squadron("sqn-60", "No.60 Squadron", "allied", "rfc", false, "Cachy"),
  squadron("sqn-85", "No.85 Squadron", "allied", "rfc", false, "St. Omer", { activeFrom: RAF_SQUADRON_ERA }),
  squadron("hd-39", "No.39 Home Defense", "allied", "rfc", false, "Sutton's Farm"),
  squadron("hd-61", "No.61 Home Defense", "allied", "rfc", false, "Rochford"),
  squadron("hd-143", "No.143 Home Defense", "allied", "rfc", false, "Ashford"),
  squadron("naval-3", "No.3 Naval Squadron", "allied", "rnas", false, "Furnes"),
  squadron("naval-8", "No.8 Naval Squadron", "allied", "rnas", false, "St. Marie Cappel"),
  squadron("esc-23", "Escadrille 23", "allied", "french", false, "Le Plessis Belleville"),
  squadron("esc-38", "Escadrille 38", "allied", "french", false, "Chateau-Thierry"),
  squadron("aero-27", "27th Aero Squadron", "allied", "american", false, "Maxville"),
  squadron("flabt-62", "Fl. Abt. No.62", "german", "gas", false, "Lille", { role: "recon" }),
  squadron("kek-2", "KEK 2", "german", "gas", false, "Metz", { activeUntil: KEK_ERA_END }),
  squadron("kek-habsheim", "KEK Habsheim", "german", "gas", false, "St. Mihiel", { activeUntil: KEK_ERA_END }),
  squadron("fokker-staffel-3", "3rd Fokker Staffel", "german", "gas", false, "Douai", {
    activeUntil: KEK_ERA_END,
  }),
  squadron("jasta-5", "Jasta 5", "german", "gas", false, "Cambrai", { activeFrom: JASTA_ERA }),
  squadron("jasta-9", "Jasta 9", "german", "gas", false, "Laon", { activeFrom: JASTA_ERA }),
  squadron("jasta-14", "Jasta 14", "german", "gas", false, "St. Quentin", { activeFrom: JASTA_ERA }),
  squadron("jasta-15", "Jasta 15", "german", "gas", false, "Vousiers", { activeFrom: JASTA_ERA }),
  squadron("jasta-24", "Jasta 24", "german", "gas", false, "Courtrai", { activeFrom: JASTA_ERA }),
  squadron("jasta-27", "Jasta 27", "german", "gas", false, "Ostend", { activeFrom: JASTA_ERA }),
  squadron("jasta-37", "Jasta 37", "german", "gas", false, "Tellancourt", { activeFrom: JASTA_ERA }),
];

/**
 * The services a career pilot can actually be posted to. Career mode enlists in
 * the R.F.C. or the German Air Service only; whether a transfer can ever land a
 * player in a French Escadrille or an American Aero Squadron is an open
 * question in docs/game/campaign.md, and this port answers it "no" rather than
 * inventing an answer.
 */
export const POSTABLE_SERVICES: Readonly<Record<Nation, readonly SquadronService[]>> = {
  allied: ["rfc", "rnas"],
  german: ["gas"],
};

export function squadronById(id: string): Squadron | undefined {
  return SQUADRONS.find((entry) => entry.id === id);
}

export function requireSquadron(id: string): Squadron {
  const found = squadronById(id);
  if (!found) throw new Error(`no squadron "${id}"`);
  return found;
}

export function isActiveOn(unit: Squadron, date: CareerDate): boolean {
  if (unit.activeFrom && compareDates(date, unit.activeFrom) < 0) return false;
  if (unit.activeUntil && compareDates(date, unit.activeUntil) >= 0) return false;
  return true;
}

export function isPostable(unit: Squadron, nation: Nation): boolean {
  return unit.nation === nation && POSTABLE_SERVICES[nation].includes(unit.service);
}

/** The units a pilot of this nation could be serving with on this date. */
export function postableSquadrons(nation: Nation, date: CareerDate): readonly Squadron[] {
  return SQUADRONS.filter((unit) => isPostable(unit, nation) && isActiveOn(unit, date));
}

/** Where a new pilot is sent: an ordinary fighter squadron, never an elite one. */
export function startingSquadrons(nation: Nation, date: CareerDate): readonly Squadron[] {
  return postableSquadrons(nation, date).filter((unit) => !unit.elite && unit.role === "fighter");
}
